import React from 'react';
import { enqueueSnackbar } from 'notistack';
import type { SupportClickEntry, SupportConsoleLog, SupportDiagnosticPayload, SupportNetworkFailure } from './types';
import { installDiagnosticInterceptors, type DiagnosticInterceptorHandles } from './diagnostic-interceptors';
import { captureDiagnosticScreenshot } from './capture-screenshot';
import { submitSupportDiagnostics } from './submit-diagnostics';
import { SupportDiagnosticContext, type SupportDiagnosticContextValue, type SupportDiagnosticPhase } from './SupportModeContext';
import { DiagnosticErrorBoundary } from './DiagnosticErrorBoundary';
import { DiagnosticStartDialog } from './DiagnosticStartDialog';
import { DiagnosticFloatingWidget } from './DiagnosticFloatingWidget';
import './support-mode.css';

const RECORD_SECONDS = 60;

const OPEN_EVENT = 'support-diagnostic-open';

function buildPayload(
  consoleLogs: SupportConsoleLog[],
  networkFailures: SupportNetworkFailure[],
  clicks: SupportClickEntry[],
  screenshotBase64: string | undefined,
): SupportDiagnosticPayload {
  return {
    version: 1,
    recordedAt: new Date().toISOString(),
    pageUrl: typeof window !== 'undefined' ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    language: typeof navigator !== 'undefined' ? navigator.language : '',
    viewport:
      typeof window !== 'undefined'
        ? { width: window.innerWidth, height: window.innerHeight }
        : { width: 0, height: 0 },
    consoleLogs,
    networkFailures,
    clicks,
    screenshotPngBase64: screenshotBase64,
  };
}

export const DiagnosticProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [phase, setPhase] = React.useState<SupportDiagnosticPhase>('idle');
  const [secondsLeft, setSecondsLeft] = React.useState(RECORD_SECONDS);

  const phaseRef = React.useRef<SupportDiagnosticPhase>('idle');
  React.useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  const recordingActiveRef = React.useRef(false);
  const sessionIdRef = React.useRef(0);
  /** Browser `setInterval` id (avoid NodeJS.Timeout vs number mismatch). */
  const timerRef = React.useRef<number | null>(null);
  const interceptorRef = React.useRef<DiagnosticInterceptorHandles | null>(null);
  const finalizingRef = React.useRef(false);

  const consoleLogsRef = React.useRef<SupportConsoleLog[]>([]);
  const networkRef = React.useRef<SupportNetworkFailure[]>([]);
  const clicksRef = React.useRef<SupportClickEntry[]>([]);

  const clearTimer = React.useCallback(() => {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const teardownInterceptors = React.useCallback(() => {
    recordingActiveRef.current = false;
    interceptorRef.current?.destroy();
    interceptorRef.current = null;
  }, []);

  const resetBuffers = React.useCallback(() => {
    consoleLogsRef.current = [];
    networkRef.current = [];
    clicksRef.current = [];
  }, []);

  const finalizeReport = React.useCallback(async () => {
    if (finalizingRef.current) return;
    if (phaseRef.current !== 'recording') return;
    finalizingRef.current = true;
    clearTimer();
    teardownInterceptors();

    const logs = [...consoleLogsRef.current];
    const net = [...networkRef.current];
    const clk = [...clicksRef.current];
    resetBuffers();

    setPhase('submitting');

    let shot: string | undefined;
    try {
      shot = await captureDiagnosticScreenshot();
    } catch {
      shot = undefined;
    }

    const payload = buildPayload(logs, net, clk, shot);

    try {
      await submitSupportDiagnostics(payload);
      enqueueSnackbar('הדוח נשלח! צוות התמיכה עודכן', { variant: 'success' });
    } catch {
      enqueueSnackbar('שליחת הדוח נכשלה. נסו שוב או פנו לתמיכה.', { variant: 'error' });
    } finally {
      finalizingRef.current = false;
      setPhase('idle');
      setSecondsLeft(RECORD_SECONDS);
    }
  }, [clearTimer, resetBuffers, teardownInterceptors]);

  const cancelRecording = React.useCallback(() => {
    sessionIdRef.current += 1;
    clearTimer();
    teardownInterceptors();
    resetBuffers();
    setPhase('idle');
    setSecondsLeft(RECORD_SECONDS);
    finalizingRef.current = false;
  }, [clearTimer, resetBuffers, teardownInterceptors]);

  const openDialog = React.useCallback(() => {
    setPhase('dialog');
  }, []);

  const closeDialog = React.useCallback(() => {
    setPhase('idle');
  }, []);

  const startRecording = React.useCallback(() => {
    sessionIdRef.current += 1;
    const sid = sessionIdRef.current;
    resetBuffers();
    recordingActiveRef.current = true;

    const pushConsole = (level: SupportConsoleLog['level'], message: string) => {
      if (!recordingActiveRef.current) return;
      consoleLogsRef.current.push({ level, message, at: new Date().toISOString() });
    };
    const pushNetwork = (entry: SupportNetworkFailure) => {
      if (!recordingActiveRef.current) return;
      networkRef.current.push(entry);
    };
    const pushClick = (entry: SupportClickEntry) => {
      if (!recordingActiveRef.current) return;
      clicksRef.current.push(entry);
    };

    interceptorRef.current = installDiagnosticInterceptors({
      isActive: () => recordingActiveRef.current && sessionIdRef.current === sid,
      pushConsole,
      pushNetwork,
      pushClick,
    });

    setPhase('recording');
    setSecondsLeft(RECORD_SECONDS);

    clearTimer();
    timerRef.current = window.setInterval(() => {
      setSecondsLeft((s) => {
        const next = s - 1;
        if (next <= 0) {
          clearTimer();
          if (sessionIdRef.current === sid && phaseRef.current === 'recording') {
            void finalizeReport();
          }
          return 0;
        }
        return next;
      });
    }, 1000);
  }, [clearTimer, finalizeReport, resetBuffers]);

  const submitNow = React.useCallback(() => {
    void finalizeReport();
  }, [finalizeReport]);

  React.useEffect(() => {
    const onOpen = () => openDialog();
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, [openDialog]);

  React.useEffect(
    () => () => {
      clearTimer();
      teardownInterceptors();
    },
    [clearTimer, teardownInterceptors],
  );

  const value = React.useMemo<SupportDiagnosticContextValue>(
    () => ({
      phase,
      secondsLeft,
      openDialog,
      closeDialog,
      startRecording,
      cancelRecording,
      submitNow,
    }),
    [phase, secondsLeft, openDialog, closeDialog, startRecording, cancelRecording, submitNow],
  );

  return (
    <SupportDiagnosticContext.Provider value={value}>
      {children}
      <DiagnosticErrorBoundary>
        <DiagnosticStartDialog />
        <DiagnosticFloatingWidget />
      </DiagnosticErrorBoundary>
    </SupportDiagnosticContext.Provider>
  );
};

export function dispatchOpenSupportDiagnosticDialog(): void {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}
